import BTThemeProvider from "@common-components/bt-theme-provider";
import { selectScheduleSlotsUsed } from "@common-reducers/UserMessageSelectors";
import { deriveScheduleWallState, evaluateScheduleGate } from "@common-reducers/free-limit-gate";
import { fetchFreeLimit, selectFreeLimitPolicy, selectFreeLimitSettled, selectFreeLimitStatus, selectWallHitKey, wallHitRecorded, wallCleared } from "@common-reducers/free-limit-slice";
import { messageLimitSelector, scheduleEntitlementSelector } from "@common-reducers/UserSelectors";
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { addScheduledMessageButtonActions } from "@common/reducers/AddScheduledMessageButtonReducer";
import { handleAddScheduledMessageCloseThunk } from "@common/thunks/UserMessagesThunks";
import { Popup } from "@extension/context/popup/components/popup/popup";
import { useMediaQuery } from '@mui/material';
import { PREVIEW_DOCK_MIN_PX } from "./scheduled-message-editor";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import EditRecurringDialogConfirmation from "./edit-recurring-dialog-confirmation";
import { recordPayFunnelEvent } from "@extension/wa-communication-layer/api/api-calls";
import { shouldFireWallHit, bumpWallExposure, getWallExposure, getOfferDeclined, setOfferDeclined, chooseWallState, adoptServerWallExposure } from "@common/paywall/wall-exposure";
import { AddScheduledMessageButtonProps } from "./schedule-message-types";
import ScheduledMessageEditorWrapper, { ScheduledMessageEditorState } from "./scheduled-message-editor-wrapper";
import { selectUserId } from '@common-reducers/UserSelectors';
import { useIsMobile } from "@common/hooks/use-is-mobile";
import FeatureAwarePaywall from '../feature-paywall/feature-aware-paywall';
import { isInPaywallHoldout } from '../feature-paywall/paywall-holdout';
import { SOFT_SCHEDULING_OFFER, SCHEDULING_HINT } from '../feature-paywall/paywall-variants';


const ScheduledMessageEditorPopup: React.FC<AddScheduledMessageButtonProps> = (props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  // FE#384 — the single responsive threshold. At/above it the preview docks
  // beside the modal; below it the modal is fluid and the preview is a sheet.
  const isWide = useMediaQuery(`(min-width:${PREVIEW_DOCK_MIN_PX}px)`);
  const isShort = useMediaQuery('(max-height:640px)');
  const dialogDirection = i18n.dir(i18n.language) === 'rtl' ? 'rtl' : 'ltr';
  // Optimized: Combine selectors to reduce subscription overhead
  const { open, slotsUsed, paidUnlimited, entitlementUnknown, message } = useAppSelector(
    state => ({
      open: state.AddScheduledMessageButtonReducer.open,
      slotsUsed: selectScheduleSlotsUsed(state),
      // Paid plans have messageLimit === Infinity and are never subject to the free caps.
      paidUnlimited: messageLimitSelector(state) === Infinity,
      // FE#167 — absence of a subscription is NOT the free plan. Until the entitlement
      // resolves we hold (see deriveScheduleWallState) instead of walling a payer.
      entitlementUnknown: scheduleEntitlementSelector(state) === 'unknown',
      message: state.AddScheduledMessageButtonReducer.message
    }),
    // Use shallow equality to prevent unnecessary re-renders
    (left, right) => left.open === right.open && left.slotsUsed === right.slotsUsed
      && left.paidUnlimited === right.paidUnlimited && left.message === right.message
      && left.entitlementUnknown === right.entitlementUnknown
  );

  const userId = useAppSelector(selectUserId);
  const inHoldoutLive = isInPaywallHoldout(userId ? String(userId) : undefined);

  // #131 T2 — the free-limit policy is owned by REDUX (free-limit-slice), fetched ONCE at
  // boot and retried there. This component only SELECTS it. Nothing about the policy's
  // lifecycle may live here: this popup is mounted lazily and WhatsApp Web tears it down
  // and remounts it constantly, so component-held fetch/retry state would reset on every
  // remount.
  //
  // FAIL CLOSED: until the policy has settled the selector serves CONTROL_POLICY (today's
  // 1-concurrent behaviour), and we render NOTHING at all — so we never wall someone
  // against a policy we haven't read, and never open the editor for someone who is in fact
  // over their cap.
  const policy = useAppSelector(selectFreeLimitPolicy);
  const freeLimitSettled = useAppSelector(selectFreeLimitSettled);
  const freeLimitStatus = useAppSelector(selectFreeLimitStatus);

  // ENSURE the policy is loaded. The boot dispatch (loadUserMessagesAndCampaignsThunk) is
  // best-effort: it is debounced and it is NOT the path the WhatsApp-Web content script
  // takes to load messages, so it can silently never run — in which case the status stays
  // 'idle', never settles, and the composer renders NOTHING for everyone. (The rig caught
  // exactly that.) This trigger is idempotent: the thunk flips status to 'loading'
  // immediately, so it cannot double-fetch, and the STATE still lives entirely in redux —
  // the component only kicks it off if nobody else has.
  useEffect(() => {
    if (userId && freeLimitStatus === 'idle') {
      dispatch(fetchFreeLimit());
    }
  }, [dispatch, userId, freeLimitStatus]);

  const gate = evaluateScheduleGate({
    slotsUsed,
    isEditingExistingMessage: !!message?._id,
    paidUnlimited,
    policy,
  });
  // Composer vs wall vs render-nothing, in one pure/tested place. The identity gate lives
  // there: the wall must not fire until userId has resolved (else a holdout user gets the
  // wrong paywall and a treatment user's wall-hit is mis-tagged 'control'). Paid users never
  // wait. (#131 T2 — caught on the rig.)
  const { awaitingPolicy, isOverLimit } = deriveScheduleWallState({
    open,
    hasIdentity: !!userId,
    freeLimitSettled,
    paidUnlimited,
    gateBlocked: gate.blocked,
    entitlementUnknown,
  });

  // Fire scheduling_wall_hit ONCE per wall episode. The "already fired" flag lives in REDUX
  // (selectWallHitKey), never a component ref: this composer is torn down and remounted
  // constantly on WhatsApp Web, so a ref-based flag would reset on remount and re-fire the
  // event while the same wall is still up — double-counting wall-hits and contaminating the
  // #131 experiment. The slice survives remounts, so the dedup holds across them.
  const wallHitKey = useAppSelector(selectWallHitKey);
  useEffect(() => {
    const wallShowing = open && isOverLimit;
    const episodeKey = wallShowing
      ? `${String(userId)}:${gate.reason}:${message?._id ?? 'new'}`
      : null;
    if (episodeKey) {
      if (wallHitKey !== episodeKey) {
        dispatch(wallHitRecorded(episodeKey));
        // `variant` is the paywall-COPY arm (#131 T1). `limit_arm`/`limit_reason` are the
        // orthogonal free-LIMIT arm (#131 T2) — separate props on purpose: folding them into
        // `variant` would destroy the T1 readout. inHoldoutLive is stable here because the
        // wall never shows before userId resolves (deriveScheduleWallState identity gate).
        // Throttled separately from the redux episode dedup above: a rapid open/close toggle
        // can mint a fresh episodeKey on every remount (a real loop hit 74 fires in 37s), so
        // shouldFireWallHit caps the actual event emission to once per 30s regardless of how
        // many distinct episodes fire in that window. `exposures` is the cumulative per-device
        // count later tasks use to pick a softer treatment past the fatigue cap.
        if (shouldFireWallHit(Date.now())) {
          const exposures = bumpWallExposure();
          // The response carries the SERVER's cumulative exposure count. `exposures` above is
          // this device's optimistic local bump, kept so the very first render has a value;
          // adopting the server figure corrects it immediately and makes the offer gate work
          // across devices instead of per-browser. Still fire-and-forget: a failed call leaves
          // the local count in place and never blocks the wall.
          recordPayFunnelEvent('scheduling_wall_hit', {
            variant: inHoldoutLive ? 'generic_holdout' : 'feature_modal',
            limit_arm: gate.arm,
            limit_reason: gate.reason,
            exposures,
          })
            .then((res) => adoptServerWallExposure(res))
            .catch(() => {}); // fire-and-forget; never block the render
        }
      }
    } else if (wallHitKey) {
      dispatch(wallCleared()); // wall closed — re-arm for the next distinct episode
    }
  }, [open, isOverLimit, userId, message?._id, inHoldoutLive, gate.arm, gate.reason, wallHitKey, dispatch]);

  // No longer "held" in a ref: the identity gate means the wall never shows before userId
  // resolves, so inHoldoutLive cannot flip mid-show. Redux-derived and remount-stable.
  const inHoldout = inHoldoutLive;

  // Option-3: past FATIGUE_CAP exposures, swap the hard price-wall for a one-time
  // free-month Pro offer, then (if declined) a quiet non-blocking hint. This does NOT
  // unblock scheduling — the 2nd message stays plan-gated; it only changes what's shown
  // in place of the wall. Read fresh on every render (no redux plumbing needed — these
  // are per-device localStorage counters, not experiment-critical like the wall-hit dedup).
  const wallState = chooseWallState({ exposures: getWallExposure(), offerDeclined: getOfferDeclined() });

  // Stable object reference to prevent unnecessary re-renders
  const stableLinkPreview = useMemo(() =>
    message?.linkPreview || {}, 
    [message?.linkPreview]
  );

  // Callback to handle state changes from wrapper - updates draft to prevent race conditions
  const handleStateChange = useCallback((draftState: ScheduledMessageEditorState) => {
    dispatch(addScheduledMessageButtonActions.updateMessageDraft({
      message: draftState.message,
      isPoll: draftState.isPoll,
      poll: draftState.poll,
      asset: draftState.asset,
      imageId: draftState.imageId,
      sendLinkPreview: draftState.sendLinkPreview,
      linkPreview: draftState.linkPreview,
      // templateId: draftState.templateId, // templateId not in UserMessage type
      dueDate: draftState.dueDate ? new Date(draftState.dueDate) : undefined,
      // BE#657 — ONLY RAISE the user-set flag (on an explicit picker interaction);
      // never downgrade it here, so an existing-message edit whose flag was set true at
      // open stays sendable even before the user touches the picker.
      ...(draftState.dueDateSetByUser ? { dueDateSetByUser: true } : {}),
      rruleset: draftState.rruleset,
      isRecurring: draftState.isRecurring,
      cancelIfReceived: draftState.cancelIfReceived,
      retryLimitDuration: draftState.retryLimitDuration,
      retryLimitUnit: draftState.retryLimitUnit,
    }));
  }, [dispatch]);

  const handleClose = () => {
    dispatch(handleAddScheduledMessageCloseThunk())
  };

  // The Crisp launcher (forced to z-index 1000000 by calendar-view) floats
  // bottom-right and would otherwise cover the pinned Schedule-Send footer (and,
  // when docked, the WhatsApp preview). FE#384 — hide it whenever the schedule
  // dialog is open at any width; restore on close/unmount.
  useEffect(() => {
    const crisp = document.getElementById('crisp-chatbox');
    if (!crisp) return;
    if (open) {
      crisp.style.setProperty('display', 'none', 'important');
    } else {
      crisp.style.removeProperty('display');
    }
    return () => { crisp.style.removeProperty('display'); };
  }, [open]);

  return (
    <BTThemeProvider>
      {open && isOverLimit && (
        <Dialog
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              recordPayFunnelEvent('wall_modal_dismissed', { feature: 'scheduling', variant: inHoldout ? 'generic_holdout' : 'feature_modal' }).catch(() => {});
              // A natural dismiss (Escape/backdrop/X) while the free-month offer is showing
              // counts as a decline too — otherwise only the explicit "Not now" button
              // recorded it, and every other over-limit open re-showed the full offer forever.
              // Report it as well as setting it locally: the server persists the decline so the
              // quiet hint follows the user to their other devices. The explicit "Not now"
              // button already fires this event; without it here, a dismiss by Escape/backdrop/X
              // would stay per-device and the offer would re-show on every other machine.
              if (wallState === 'offer') {
                setOfferDeclined();
                recordPayFunnelEvent('wall_modal_dismissed', {
                  feature: 'scheduling', variant: 'soft_offer',
                }).catch(() => {});
              }
              handleClose();
            }
          }}
        >
          <DialogContent
            className="max-w-md"
            style={{
              direction: dialogDirection,
              backgroundColor: 'var(--color-surface, #ffffff)',
              color: 'var(--color-text, #111827)',
            }}
          >
            <DialogTitle className="sr-only">
              {inHoldout
                ? t('scheduledMessage.limits.freeScheduleCap')
                : wallState === 'offer'
                  ? t('paywall.softOffer.title', SOFT_SCHEDULING_OFFER.title)
                  : wallState === 'hint'
                    ? t('paywall.hint', SCHEDULING_HINT)
                    : t('paywall.scheduling.title', 'Schedule as many as you want')}
            </DialogTitle>
            {/* KEY NAME, NOT THE COPY. This was `limits.oneMessageAtATime`, whose
                VALUE has said "Three scheduled messages at a time" in all nine
                locales since #131 T2 concluded (2026-07-22) and made 3 the
                universal free baseline. A reviewer reading the call site
                reasonably concluded the holdout dialog contradicted the
                paywall's "line up 3 at once"; it never did — the identifier was
                the only thing still saying one. Renamed so the next reader is
                not sent chasing a contradiction that is not on screen.
                The number itself lives in `FREE_BASELINE_POLICY`
                (free-limit-gate.ts), which mirrors the backend's
                `FREE_DEFAULT_LIMITS`. */}
            {inHoldout ? (
              <Popup
                headerTitle={t('scheduledMessage.limits.freeScheduleCap')}
                headerSubtitle={t('scheduledMessage.limits.goPremiumForUnlimited')}
                onClose={handleClose}
              />
            ) : (
              <FeatureAwarePaywall feature="scheduling" onClose={handleClose} wallState={wallState} />
            )}
          </DialogContent>
        </Dialog>
      )}
      {/* FE#384 — the schedule dialog uses its OWN responsive tabbed shell (the
          tabbed editor owns the modal card AND the docked/sheet preview), so it
          does NOT use the shared EditorPreviewDialog (that stays for the campaign
          message editors). Height-capped to the viewport. */}
      <Dialog
        open={open && !isOverLimit && !awaitingPolicy}
        onOpenChange={(open) => !open && handleClose()}
      >
        <DialogContent
          showCloseButton={false}
          className={isMobile ? "p-0 bt-editor-mobile" : "p-0"}
          style={isMobile ? {
            // Full-screen sheet on phones: use the whole viewport, contain any
            // horizontal spill (no more left/right clip), and let the editor own
            // its internal vertical scroll + sticky footer.
            direction: dialogDirection,
            backgroundColor: 'var(--color-surface)',
            overflow: 'hidden',
            maxWidth: '100vw',
            width: '100vw',
            maxHeight: '100dvh',
            height: '100dvh',
            border: 'none',
            borderRadius: 0,
          } : {
            // FE#384 responsive shell. The modal is HEIGHT-CAPPED to the viewport
            // (the core fix) — its content region owns the only scroll and the
            // pinned header/footer never fall off screen. Width: fluid (capped
            // 760px) when compact, and auto (fits modal 680 + gap + docked preview
            // 344) when wide. The card + preview carry their own surface/shadow, so
            // the DialogContent itself is a transparent, borderless, padby-0 frame.
            direction: dialogDirection,
            backgroundColor: 'transparent',
            overflow: 'visible',
            border: 'none',
            boxShadow: 'none',
            padding: 0,
            width: isWide ? 'auto' : 'min(760px, 96vw)',
            maxWidth: isWide ? '96vw' : 'min(760px, 96vw)',
            // Capped at 800px: past that the text box only grows taller with
            // nothing in it. Radix keeps the dialog centred at any height.
            height: `min(800px, calc(100dvh - ${(isShort ? 14 : 20) * 2}px))`,
            maxHeight: `min(800px, calc(100dvh - ${(isShort ? 14 : 20) * 2}px))`,
          }}
        >
          <DialogTitle className="sr-only">{t('calendar.title', 'Schedule message')}</DialogTitle>
          {/* Only render heavy components when dialog is actually open. The
              wrapper hydrates redux and renders the responsive tabbed editor,
              which owns BOTH the modal card and the WhatsApp preview (docked when
              wide, a slide-in sheet when compact). */}
          {open && !isOverLimit && !awaitingPolicy && (
            <ScheduledMessageEditorWrapper
              incomingMessage={message?.message || ''}
              incomingIsPoll={message?.isPoll || false}
              incomingPoll={message?.poll ?? {}}
              incomingAsset={message?.asset ?? { url: '' }}
              incomingImageId={message?.imageId ?? ''}
              incomingSendLinkPreview={message?.sendLinkPreview || false}
              incomingLinkPreview={stableLinkPreview}
              incomingTemplateId={''} // templateId not in UserMessage, safe to pass empty string
              incomingDueDate={message?.dueDate ? new Date(message.dueDate).toISOString() : ''}
              incomingRruleset={message?.rruleset ?? ''}
              incomingIsRecurring={message?.isRecurring || false}
              incomingCancelIfReceived={message?.cancelIfReceived || false}
              incomingRetryLimitDuration={message?.retryLimitDuration}
              incomingRetryLimitUnit={message?.retryLimitUnit}
              onStateChange={handleStateChange}
              isPreview={false}
              editorContext="scheduleMessage"
            />
          )}
        </DialogContent>
      </Dialog>

      <EditRecurringDialogConfirmation />
    </BTThemeProvider>
  );
};

export default ScheduledMessageEditorPopup;




/*
  const handleMessageChange = async (msg: UserMessage) => {
    await dispatch(handleMessageChangeThunk({ message, msg }));
  };

// Poll-related effects
  useEffect(() => {
    const updatePollMessage = async () => {
      if (pollFromWAModal) {
        setEditorType('poll');
        setLocalPollState(pollFromWAModal);
        await handleMessageChange({ ...message, poll: pollFromWAModal, isPoll: true });
      }
    };
    updatePollMessage();
  }, [pollFromWAModal]);

  useEffect(() => {
    const updateDebouncedPoll = async () => {
      if (debouncedInternalPollValue) {
        if (editorType === 'poll') {
          await handleMessageChange({ poll: localPollState, isPoll: true });
        }
      }
    };
    updateDebouncedPoll();
  }, [debouncedInternalPollValue]);

  useEffect(() => {
    setEditorType(message?.isPoll ? 'poll' : 'message');
  }, [message?.isPoll]);

    const [localPollState, setLocalPollState] = useState<Partial<Poll>>(initialPoll);
  const [editorType, setEditorType] = useState<'message' | 'poll'>('message');
  const debouncedInternalPollValue = useDebounce(localPollState, 500);

      pollFromWAModal

    pollFromWAModal: state.AddScheduledMessageButtonReducer.pollFromWAModal,

  */
